"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { INSURANCE_CARD_CACHE_KEY } from "@/src/lib/data-fetchers/insurance-card-fetchers";
import {
  ORDER_PPN_RECOMMENDATIONS_CACHE_KEY,
  ORDER_STEDI_ELIGIBILITY_CHECK_CACHE_KEY,
} from "@/src/lib/data-fetchers/order-query-keys";
import type { EligibilityCoverage } from "@/src/models/stedi-eligibility-check";
import {
  getEligibilityCheckByOrderId,
  getEligibilityCheckByReferralId,
  getEligibilityCheckByTaskId,
  recheckOrderEligibility,
  recheckTaskEligibility,
  verifyInsuranceEligibilityAtIntake,
  type VerifyInsuranceEligibilityRequestPayload,
} from "@/src/requests/stedi-eligibility-check";

export const STEDI_ELIGIBILITY_CHECK_CACHE_KEY = "stedi-eligibility-check";
export const INTAKE_STEDI_ELIGIBILITY_CHECK_CACHE_KEY = "intake-stedi-eligibility-check";

function eligibilityQueryKey(key: string, id: string | null, coverage: EligibilityCoverage | null) {
  return [key, id, coverage?.id ?? null, coverage?.order ?? null];
}

export function useStediEligibilityCheck(referralId: string | null, coverage: EligibilityCoverage | null) {
  const query = useQuery({
    queryKey: eligibilityQueryKey(STEDI_ELIGIBILITY_CHECK_CACHE_KEY, referralId, coverage),
    queryFn: () => {
      if (!referralId) return null;

      return getEligibilityCheckByReferralId(referralId, coverage);
    },
    enabled: !!referralId && coverage !== null,
  });

  return {
    eligibilityCheck: query.data ?? null,
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export function useRecheckReferralEligibility(taskId: string, referralId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (coverage: EligibilityCoverage | null) => recheckTaskEligibility(taskId, coverage),
    onSuccess: (data, coverage) => {
      if (data.check) {
        queryClient.setQueryData(
          eligibilityQueryKey(STEDI_ELIGIBILITY_CHECK_CACHE_KEY, referralId, coverage),
          data.check
        );

        return;
      }

      void queryClient.invalidateQueries({
        queryKey: eligibilityQueryKey(STEDI_ELIGIBILITY_CHECK_CACHE_KEY, referralId, coverage),
      });
    },
  });
}

export function useIntakeStediEligibilityCheck(taskId: string | null, coverage: EligibilityCoverage | null) {
  const query = useQuery({
    queryKey: eligibilityQueryKey(INTAKE_STEDI_ELIGIBILITY_CHECK_CACHE_KEY, taskId, coverage),
    queryFn: () => {
      if (!taskId) return null;

      return getEligibilityCheckByTaskId(taskId, coverage);
    },
    enabled: !!taskId && coverage !== null,
  });

  return {
    eligibilityCheck: query.data ?? null,
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export function useVerifyInsuranceEligibilityAtIntake(taskId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: VerifyInsuranceEligibilityRequestPayload) => {
      if (!taskId) throw new Error("Task id is required to verify insurance eligibility");

      return verifyInsuranceEligibilityAtIntake(taskId, payload);
    },
    onSuccess: (data, payload) => {
      if (!taskId) return;

      queryClient.setQueryData(
        eligibilityQueryKey(INTAKE_STEDI_ELIGIBILITY_CHECK_CACHE_KEY, taskId, payload.coverage),
        data.check
      );
    },
  });
}

export function useOrderStediEligibilityCheck(orderId: string | null, coverage: EligibilityCoverage | null) {
  const query = useQuery({
    queryKey: eligibilityQueryKey(ORDER_STEDI_ELIGIBILITY_CHECK_CACHE_KEY, orderId, coverage),
    queryFn: () => {
      if (!orderId) return null;

      return getEligibilityCheckByOrderId(orderId, coverage);
    },
    enabled: !!orderId && coverage !== null,
  });

  return {
    eligibilityCheck: query.data ?? null,
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export function useRecheckOrderEligibility(orderId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (coverage: EligibilityCoverage | null) => {
      if (!orderId) {
        throw new Error("Order id is required to re-check insurance eligibility");
      }

      return recheckOrderEligibility(orderId, coverage);
    },
    onSuccess: (data, coverage) => {
      if (!orderId) return;

      void queryClient.invalidateQueries({ queryKey: [ORDER_PPN_RECOMMENDATIONS_CACHE_KEY, orderId] });
      void queryClient.invalidateQueries({ queryKey: [INSURANCE_CARD_CACHE_KEY] });

      if (data.check) {
        queryClient.setQueryData(
          eligibilityQueryKey(ORDER_STEDI_ELIGIBILITY_CHECK_CACHE_KEY, orderId, coverage),
          data.check
        );

        return;
      }

      void queryClient.invalidateQueries({
        queryKey: eligibilityQueryKey(ORDER_STEDI_ELIGIBILITY_CHECK_CACHE_KEY, orderId, coverage),
      });
    },
  });
}
